import { FunctionComponent, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ListItem } from '@chakra-ui/react';
import { publicApi, PublicAttributeGetEntryListResponseEntry } from '@netiva/classifieds-api';
import { useDebounce } from '@netiva/classifieds-common';
import { Typeahead } from '@netiva/classifieds-ui';

import { useAppDispatch, useAppSelector } from '@/store';
import { adActions } from '@/store/ad';
import { AdAttribute } from '@/store/ad/types';

export type TextTypeaheadProps = {
  attribute: AdAttribute;
  parentAttribute?: AdAttribute;
};

export const TextTypeahead: FunctionComponent<TextTypeaheadProps> = ({ attribute, parentAttribute }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { formData } = useAppSelector((state) => state.ad);

  const parentEntries = useMemo(
    () => (parentAttribute ? formData[parentAttribute.id]?.entries : []) || [],
    [formData, parentAttribute]
  );

  const value = useMemo(() => (attribute && formData[attribute.id]?.value) || '', [attribute, formData]);
  const debouncedValue = useDebounce(value, 200);
  const { data, isFetching } = publicApi.useGetAttributeEntries({
    dataAttributeId: attribute.id,
    filter: debouncedValue,
    parentEntryIds: parentEntries,
    maxCount: 8,
  });

  const items = useMemo(() => {
    if (!data?.items) {
      return [];
    }

    let filteredItems = data.items;
    if (parentAttribute) {
      filteredItems = filteredItems.filter((item) => !item.parentEntryId || parentEntries.includes(item.parentEntryId));
    }
    return filteredItems;
  }, [data, parentAttribute, parentEntries]);

  useEffect(() => {
    if (attribute && items.length === 1 && items[0].name.toLowerCase() === value.toLowerCase()) {
      dispatch(adActions.updateFormData({ key: attribute.id, value: items[0].name, entries: [items[0].id] }));
    }
  }, [attribute, dispatch, items, value]);

  const handleOnChange = (value: string) => {
    if (attribute) {
      const entry = items.find((item) => item.name.toLowerCase() === value.toLowerCase());
      const entries = entry ? [entry.id] : [];
      dispatch(adActions.updateFormData({ key: attribute.id, value: entry ? entry.name : value, entries }));
    }
  };

  const handleItemSelected = (item: PublicAttributeGetEntryListResponseEntry) => {
    if (attribute) {
      dispatch(adActions.updateFormData({ key: attribute.id, value: item.name, entries: [item.id] }));
    }
  };

  if (!attribute) {
    return null;
  }

  return (
    <Typeahead
      value={value}
      items={items}
      onChange={(e) => handleOnChange(e.target.value)}
      onItemSelected={handleItemSelected}
      isLoading={isFetching}
      maxLength={attribute.maxLength || undefined}
      renderItem={(item, onSelect, isHovered) => {
        const text = item.name;
        return (
          <ListItem
            key={item.id}
            onClick={() => onSelect(item)}
            cursor="pointer"
            padding={2}
            fontWeight={text === value ? 'bold' : 'normal'}
            _hover={{
              backgroundColor: 'surface',
            }}
            backgroundColor={isHovered ? 'surface' : 'transparent'}
          >
            {text}
          </ListItem>
        );
      }}
      emptyValueItem={
        <ListItem padding={2} color="neutral.400" textAlign="center">
          {t('ad.steps.attributes.typeaheadInfo')}
        </ListItem>
      }
    />
  );
};
